package top.wcpe.allincore.ui.runtime.ir.api

/**
 * v4 §E.3 世界数据源 runtime SPI。
 *
 * 本接口是 `client.world.*` DSL 作用域的数据契约，由各平台客户端层（fabric / neoforge）
 * 适配实现并注入到 EvalContext（jvmMain，避免 commonMain KDoc 悬空链接故不用 `[]`）。
 *
 * **依赖方向**：`ui-client-shared` 依赖 `ui-runtime`（不可反向），因此世界采集 SPI
 * （`WorldSnapshotProvider` 等）位于 client-shared 的接口无法被 runtime 直接引用。
 * 故在 runtime 侧定义本最小 SPI；平台层用 adapter 把 client-shared 的 `WorldSnapshotProvider`
 * 适配到本接口（DTO → DSL 友好的 `List<Number>` / `List<Map>`），实现解耦。
 *
 * **返回类型约定**：所有方法直接返回 DSL 运行期可消费的纯 JVM 类型（`Double` / `String` /
 * `List<Number>` / `List<Map>`），不引入额外 DTO，让 `ExprEvaluator` 与 DSL `for` 循环、属性访问无缝衔接。
 *
 * **线程契约**：与 vanilla `level.getChunk` 一致，仅在客户端主线程调用。
 */
interface WorldDataSource {

    /** 玩家世界 X 坐标。 */
    fun posX(): Double

    /** 玩家世界 Y 坐标。 */
    fun posY(): Double

    /** 玩家世界 Z 坐标。 */
    fun posZ(): Double

    /** 玩家朝向 yaw（度）。 */
    fun yaw(): Double

    /** 当前维度 namespaced key（如 `"minecraft:overworld"`）；无世界时返回空串。 */
    fun dimension(): String

    /**
     * 玩家周围 (2*radius)² 个 tile 的颜色 byte 列表（懒计算 + 缓存由实现方负责）。
     *
     * @param radius 半径（tile 数；矩阵边长 = 2*radius）
     * @return 按行优先排列的 tile 颜色 byte（装箱为 [Number]）；无数据返回空列表
     */
    fun tiles(radius: Int): List<Number>

    /**
     * 玩家周围实体列表，每个实体为含 `dx/dz/dy/kind/name/facing` 键的 Map。
     *
     * @param radius 半径（方块数）
     * @return 实体 Map 列表；无数据返回空列表（值恒非空：dx/dz/dy 为 Float、kind/name/facing 为 String）
     */
    fun entities(radius: Int): List<Map<String, Any>>

    /** 当前是否单机世界（mc.hasSingleplayerServer()）；无世界返回 false。 */
    fun isSingleplayer(): Boolean = false

    /** 单机世界是否已发布到局域网（mc.getSingleplayerServer().isPublished）；非单机返回 false。 */
    fun isPublished(): Boolean = false

    /** 当前是否连接到本地服务器（mc.isLocalServer()）；无世界返回 false。 */
    fun isLocalServer(): Boolean = false

    companion object {
        /**
         * 默认空数据源：坐标 / 朝向归零，维度空串，tile / entity 返回空列表。
         *
         * 保证未注入真实平台实现的链路（测试 / mockServer / 服务端）零改动可运行，
         * DSL 读 `client.world.posX` 得 0、`client.world.entities(48)` 得空列表。
         */
        @Suppress("MagicNumber")
        val NOOP: WorldDataSource = object : WorldDataSource {
            override fun posX(): Double = 0.0
            override fun posY(): Double = 0.0
            override fun posZ(): Double = 0.0
            override fun yaw(): Double = 0.0
            override fun dimension(): String = ""
            override fun tiles(radius: Int): List<Number> = emptyList()
            override fun entities(radius: Int): List<Map<String, Any>> = emptyList()
        }
    }
}
